import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import '@ant-design/compatible/assets/index.css';
import { Select, Spin, Radio, Modal, Form, Alert, Checkbox, Switch } from 'antd';
import * as React from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	approvalRulesRestCrudThunksForBuyer,
	approvalRulesRestCrudThunksForSupplier,
	approvalsRestCrudThunksForBuyer,
	approvalsRestCrudThunksForSupplier,
} from '../../thunks/company_approval_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { useMemo, useState } from 'react';
import OWAsyncTreeSelect from '../ow_async_tree_select/OWAsyncTreeSelect';
import {
	workOrderPrioritiesRestCrudThunksForBuyer,
	workOrderPrioritiesRestCrudThunksForSupplier,
} from '../../thunks/work_order_priorities_thunks';
import _ from 'lodash';
import {
	mappingApprovalRuleComparisonData,
	mappingURLParamsToAddApprovalButton,
} from '../../utils/DataMappingUtils';
import ProblemTypeTreeSelect from '../problem_type_tree_select';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesHeirarchicalForSupplier,
	problemTypesRestCrudThunksForBuyer,
	problemTypesRestCrudThunksForSupplier,
} from '../../thunks/problem_types_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import {
	approvalRuleTypes,
	getEntityRequestApprovalRuleOptions,
} from '../../utils/ApprovalHierarchyUtils';
import { contactsRestCrudThunksForSupplierForThirdPartySuppliers } from '../../thunks/contacts_thunks';
import RegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import SelectWithAllToggle from '../common/SelectWithAllToggle';

const WORK_ORDER_FORM_PROBLEM_TYPES_AUTOCOMPLETE = 'WORK_ORDER_FORM_PROBLEM_TYPES_AUTOCOMPLETE';

const approvalRuleOptions = {
	IS_CAPEX: 'isCapex',
	IS_PM: 'isPM',
	WORK_ORDER_PRIORITIES: 'woPriorityIds',
	PROBLEM_TYPES: 'problemTypeIds',
	LOCATION: 'locationIds',
	STOCK_LOCATION: 'stockLocationIds',
	FIELD_TECHS: 'fieldTechEmails',
	ENTITY_TYPES: 'entityTypes',
};

const getRequiredList = (type, allowLocationFilter = false) => {
	if (type === approvalRuleTypes.PURCHASE_REQUEST) {
		return [approvalRuleOptions.STOCK_LOCATION, approvalRuleOptions.FIELD_TECHS];
	}
	// A suggested-entity request carries none of the work-order attributes — the only thing a rule
	// can key on is which entity type was requested.
	if (type === approvalRuleTypes.ENTITY_REQUEST) {
		return [approvalRuleOptions.ENTITY_TYPES];
	}
	const list = [
		approvalRuleOptions.IS_CAPEX,
		approvalRuleOptions.IS_PM,
		approvalRuleOptions.WORK_ORDER_PRIORITIES,
		approvalRuleOptions.PROBLEM_TYPES,
	];
	if (allowLocationFilter) {
		list.push(approvalRuleOptions.LOCATION);
	}
	return list;
};

function ApprovalRuleModal({
	visible,
	record,
	existingRules,
	hideModal,
	currentUser,
	approvalHierarchyType,
	isDefault,

	workOrderPriorities,
	fetchWorkOrderPriorities,
	fetchMultipleWorkOrderPriorities,

	problemTypes,
	fetchProblemTypes,
	fetchMultipleProblemTypes,

	locations,
	fetchMultipleLocations,

	stockLocations,
	fetchStockLocations,
	fetchMultipleStockLocations,

	fieldTechs,
	fetchFieldTechs,

	editSuccess,
	createSuccess,

	hierarchiesFetching,
	hierarchies,
	fetchHierarchyTemplates,
	type,
	allowLocationFilter,
}) {
	const [form] = Form.useForm();
	const isUpdate = !!nullSafeGet('id', record);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState(undefined);

	useMemo(() => {
		fetchHierarchyTemplates({ limit: 50, offset: 0 });
	}, []);

	// "Select All" (or an empty selection) means the rule is not filtered on that dimension, so we
	// submit `undefined` — the canonical "no constraint" shape, which FetchUtils.sanitize drops from
	// the body so it lands as NULL. This is safe on update because the endpoint full-replaces every
	// condition field from the request payload (it does not merge), so an omitted key clears any
	// previously-saved filter rather than leaving it in place.
	const normalizeAllSelected = (allSelected, ids) =>
		allSelected || !ids || ids.length === 0 ? undefined : ids;

	// An axis is "no condition" when it has no ids selected; treat an empty array the same as unset
	// so validation stays correct regardless of whether a field hands us `[]` or `undefined`.
	const isConditionEmpty = (val) => val === undefined || (Array.isArray(val) && val.length === 0);

	const handleSubmit = (values) => {
		let approvalRule = {
			id: values.id,
			[approvalRuleOptions.WORK_ORDER_PRIORITIES]: normalizeAllSelected(
				values.allWOPriorities,
				values[approvalRuleOptions.WORK_ORDER_PRIORITIES]
			),
			[approvalRuleOptions.PROBLEM_TYPES]: normalizeAllSelected(
				values.allProblemTypes,
				values[approvalRuleOptions.PROBLEM_TYPES]
			),
			[approvalRuleOptions.IS_CAPEX]: values[approvalRuleOptions.IS_CAPEX],
			[approvalRuleOptions.IS_PM]: values[approvalRuleOptions.IS_PM],
			[approvalRuleOptions.LOCATION]: values[approvalRuleOptions.LOCATION],
			[approvalRuleOptions.STOCK_LOCATION]: values[approvalRuleOptions.STOCK_LOCATION],
			[approvalRuleOptions.FIELD_TECHS]: values[approvalRuleOptions.FIELD_TECHS],
			[approvalRuleOptions.ENTITY_TYPES]: values[approvalRuleOptions.ENTITY_TYPES],
			approvalHierarchyId: values.approvalHierarchyId,
			approvalHierarchyType: approvalHierarchyType,
			isDefault,
			autoApproveIfNoUserHasAccess: !!values.autoApproveIfNoUserHasAccess,
			sequentialApprovalEnforcement: !!values.sequentialApprovalEnforcement,
		};
		if (
			// A default rule is a singleton distinguished by `isDefault`, not by its conditions — it
			// carries none, so its comparison shape is all-empty. Running the duplicate check against
			// it would falsely match any existing condition-less rule ("Approval Rule with these
			// conditions already exists"), so skip it for defaults, exactly like the condition check below.
			!isDefault &&
			existingRules.some(
				(er) =>
					_.isEqual(
						mappingApprovalRuleComparisonData(er),
						mappingApprovalRuleComparisonData(approvalRule)
					) && er.id !== approvalRule.id
			)
		) {
			setErrorMessage('Error: Approval Rule with these conditions already exists.');
		} else if (
			getRequiredList(type, allowLocationFilter).every((val) =>
				isConditionEmpty(approvalRule[val])
			) &&
			!isDefault
		) {
			setErrorMessage('Error: You must select a condition.');
		} else {
			setButtonLoading(true);
			const submitRule = isUpdate ? editSuccess : createSuccess;
			submitRule(approvalRule)
				.then(() => {
					setButtonLoading(false);
					setErrorMessage(undefined);
					hideModal();
				})
				.catch((error) => {
					// Surface the failure instead of swallowing it — otherwise a rejected save leaves
					// the button spinning with no feedback, which reads as "nothing happened".
					setButtonLoading(false);
					setErrorMessage(
						typeof error === 'string' && error
							? `Error: ${error}`
							: 'Error: Something went wrong saving this approval rule. Please try again.'
					);
				});
		}
	};

	const hierarchiesDropdownRecords = getRecordsForTargetCollection(
		hierarchies,
		'APPROVAL_HIERARCHY_AUTOCOMPLETE'
	);

	const autoApproveIfNoUserHasAccess = !!nullSafeGet('autoApproveIfNoUserHasAccess', record);
	const sequentialApprovalEnforcement = !!nullSafeGet('sequentialApprovalEnforcement', record);

	const formItems =
		type === approvalRuleTypes.PURCHASE_REQUEST ? (
			<>
				<Form.Item
					label={'Stock Locations'}
					name={approvalRuleOptions.STOCK_LOCATION}
					initialValue={record.stockLocationIds}
					hidden={isDefault}
				>
					<OWAsyncSelect
						mode="multiple"
						emptyWhenDisabled={true}
						stateSlice={stockLocations}
						targetCollectionName={'approvalRulesIndex'}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleStockLocations(ids, targetCollectionName);
						}}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchStockLocations(
								{ name: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={(d) => (
							<Select.Option value={d.id} key={d.id}>
								{d.name}
							</Select.Option>
						)}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
					/>
				</Form.Item>
				<Form.Item
					label={'Field Technicians'}
					name={approvalRuleOptions.FIELD_TECHS}
					initialValue={record[approvalRuleOptions.FIELD_TECHS]}
					hidden={isDefault}
				>
					<OWAsyncSelect
						style={{ maxWidth: 640 }}
						mode="multiple"
						stateSlice={fieldTechs}
						valueAccessor={(el) => el.email}
						targetCollectionName="fieldTechForm"
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchFieldTechs(
								{ search: searchText && searchText.trim() },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						fetchMultiple={(...args) => fetchFieldTechs(...args)}
						renderRecord={(ft) => (
							<Select.Option key={ft.email} value={ft.email}>
								{nullSafeGet('contact.nameGiven', ft)} {nullSafeGet('contact.nameFamily', ft)} (
								{nullSafeGet('contact.email', ft)})
							</Select.Option>
						)}
						sortBy={{
							sort_by: 'name',
							order: 'ascend',
						}}
					/>
				</Form.Item>
			</>
		) : type === approvalRuleTypes.ENTITY_REQUEST ? (
			<Form.Item
				label={'Entity Types'}
				name={approvalRuleOptions.ENTITY_TYPES}
				initialValue={record[approvalRuleOptions.ENTITY_TYPES]}
				hidden={isDefault}
			>
				<Select
					mode="multiple"
					style={{ maxWidth: 640 }}
					placeholder="Select entity types"
					options={getEntityRequestApprovalRuleOptions(nullSafeGet('userType', currentUser))}
				/>
			</Form.Item>
		) : (
			<>
				<Form.Item
					label={'Expenditure Type'}
					name={'isCapex'}
					initialValue={record.isCapex}
					hidden={isDefault}
				>
					<Radio.Group>
						<Radio.Button style={{ flexWrap: 'nowrap' }} value={undefined}>
							Any
						</Radio.Button>
						<Radio.Button style={{ flexWrap: 'nowrap' }} value={false}>
							R&M
						</Radio.Button>
						<Radio.Button style={{ flexWrap: 'nowrap' }} value={true}>
							CapEx
						</Radio.Button>
					</Radio.Group>
				</Form.Item>
				<Form.Item
					name={'isPM'}
					label={'Work Order Type'}
					initialValue={record.isPM}
					hidden={isDefault}
				>
					<Radio.Group>
						<Radio.Button style={{ flexWrap: 'nowrap' }} value={undefined}>
							Any
						</Radio.Button>
						<Radio.Button style={{ flexWrap: 'nowrap' }} value={true}>
							Planned
						</Radio.Button>
						<Radio.Button style={{ flexWrap: 'nowrap' }} value={false}>
							Reactive
						</Radio.Button>
					</Radio.Group>
				</Form.Item>
				<div hidden={isDefault}>
					<SelectWithAllToggle
						label={'Work Order Priority'}
						name={'woPriorityIds'}
						allName={'allWOPriorities'}
						form={form}
						selectAllPlaceholder={'All work order priorities'}
					>
						<OWAsyncSelect
							mode={'multiple'}
							fetchData={fetchWorkOrderPriorities}
							fetchMultiple={(ids, targetCollectionName) => {
								fetchMultipleWorkOrderPriorities(ids, targetCollectionName);
							}}
							renderRecord={(workOrderPriority) => (
								<Select.Option key={workOrderPriority.id} value={workOrderPriority.id}>
									{workOrderPriority.name}
								</Select.Option>
							)}
							stateSlice={workOrderPriorities}
							targetCollectionName={'approvalRulesIndex'}
						/>
					</SelectWithAllToggle>
				</div>
				<div hidden={isDefault}>
					<SelectWithAllToggle
						label={'Problem Type'}
						name={'problemTypeIds'}
						allName={'allProblemTypes'}
						form={form}
						selectAllPlaceholder={'All problem types'}
					>
						<ProblemTypeTreeSelect
							mode="multiple"
							collapseSelectionForDisplay
							stateSlice={problemTypes}
							targetCollectionName={WORK_ORDER_FORM_PROBLEM_TYPES_AUTOCOMPLETE}
							fetchMultiple={(ids, targetCollectionName) => {
								fetchMultipleProblemTypes(ids, targetCollectionName);
							}}
							fetchData={(
								searchText,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) =>
								fetchProblemTypes(
									{ name: searchText },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								)
							}
							additionalFilters={{}}
							sortBy={{ sort_by: 'name', order: 'ascend' }}
						/>
					</SelectWithAllToggle>
				</div>
				{allowLocationFilter ? (
					<Form.Item
						name="locationIds"
						label="Location"
						initialValue={nullSafeGet('locationIds', record)}
						hidden={isDefault}
					>
						<RegionsLocationsTreeSelect
							mode="multiple"
							targetCollectionName="approvalRulesLocations"
							fetchMultiple={(ids, targetCollectionName) => {
								fetchMultipleLocations(ids, targetCollectionName);
							}}
						/>
					</Form.Item>
				) : null}
			</>
		);

	return (
		<Modal
			visible={visible}
			width={600}
			title={
				isDefault
					? 'Select Default Approval Hierarchy'
					: isUpdate
					? 'Update Approval Rule'
					: 'Add Approval Rule'
			}
			okText={isUpdate ? 'Update Rule' : 'Add Rule'}
			onOk={form.submit}
			confirmLoading={buttonLoading}
			onCancel={hideModal}
			closable={true}
			destroyOnClose={true}
		>
			<Form
				layout="vertical"
				form={form}
				preserve={false}
				initialValues={{
					woPriorityIds: nullSafeGet('woPriorityIds', record),
					problemTypeIds: nullSafeGet('problemTypeIds', record),
					// Default the "Select All" toggle on when the rule isn't filtered on that dimension
					// (no ids saved) — that empty state already means "applies to all".
					allWOPriorities: !nullSafeGetOrElse('woPriorityIds', record, []).length,
					allProblemTypes: !nullSafeGetOrElse('problemTypeIds', record, []).length,
				}}
				onFinish={handleSubmit}
			>
				<Form.Item hidden={errorMessage === undefined}>
					<Alert message={errorMessage} type="error" />
				</Form.Item>
				<h6 hidden={isDefault}>If {mappingURLParamsToAddApprovalButton(type)} is</h6>
				<Form.Item name={'id'} hidden={true} initialValue={record.id}></Form.Item>
				{formItems}
				<h6 hidden={isDefault}>then use</h6>
				<Form.Item
					label="Approval Hierarchy"
					name="approvalHierarchyId"
					rules={[{ required: true, message: 'This field is required.' }]}
					initialValue={nullSafeGet('approvalHierarchyId', record)}
				>
					<Select
						notFoundContent={hierarchiesFetching ? <Spin size="small" /> : null}
						placeholder="Type to search for an approval hierarchy"
						filterOption={false}
						showSearch={true}
					>
						{hierarchiesDropdownRecords.map((d) => (
							<Select.Option value={d.id} key={d.id}>
								<div>{d.name}</div>
							</Select.Option>
						))}
					</Select>
				</Form.Item>
				{compareStrings(type, 'workOrder') && !isDefault ? (
					<div className="flex flex-row items-baseline">
						<Form.Item
							name={'autoApproveIfNoUserHasAccess'}
							initialValue={autoApproveIfNoUserHasAccess}
							valuePropName="checked"
						>
							<Switch />
						</Form.Item>
						<label className="ml-2">Auto-approve when no approver found</label>
					</div>
				) : null}
				<div className="flex flex-row items-baseline">
					<Form.Item
						name={'sequentialApprovalEnforcement'}
						initialValue={sequentialApprovalEnforcement}
						valuePropName="checked"
					>
						<Switch />
					</Form.Item>
					<label className="ml-2">Enforce sequential approval</label>
				</div>
			</Form>
		</Modal>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	isApprovalForm: ownProps.isApprovalForm,
	createErrors: state.work_orders.createErrors,
	updateErrors: state.work_orders.updateErrors,
	formData: ownProps.formData,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,
	currentUser: state.session.currentUser,
	hierarchies: state.approval_hierarchies,
	hierarchiesFetching: state.approval_hierarchies.fetching,
	workOrderPriorities: state.work_order_priorities,
	problemTypes: state.problem_types,
	locations: state.locations,
	stockLocations: state.stock_locations,
	fieldTechs: state.all_contacts,
	allowLocationFilter: nullSafeGetOrElse(
		'company_config.detail.config.approvalHierarchyConfig.allowLocationFilterInApprovalHierarchy',
		state,
		false
	),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchApprovalRule: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? approvalRulesRestCrudThunksForSupplier.readOne(id)
				: approvalRulesRestCrudThunksForBuyer.readOne(id)
		),
	fetchWorkOrderPriorities: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		return dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrderPrioritiesRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: workOrderPrioritiesRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		);
	},
	fetchMultipleWorkOrderPriorities: (ids, targetCollectionName) =>
		dispatch(workOrderPrioritiesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesHeirarchicalForSupplier(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: problemTypesHeirarchicalForBuyer(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: problemTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: locationsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchFieldTechs: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			contactsRestCrudThunksForSupplierForThirdPartySuppliers.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection,
				(data) => data.map((user) => ({ ...user, email: user.id }))
			)
		),
	fetchHierarchyTemplates: (params) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? approvalsRestCrudThunksForSupplier.read(params, 'APPROVAL_HIERARCHY_AUTOCOMPLETE')
				: approvalsRestCrudThunksForBuyer.read(params, 'APPROVAL_HIERARCHY_AUTOCOMPLETE')
		),
});

const ApprovalRuleModalWR = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ApprovalRuleModal)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ApprovalRuleModalWR);
